import { createAppRequest } from '../request';

export interface ApiResponse<T = unknown> {
  code: number;
  msg: string;
  data: T;
}

export interface PageResult<T = unknown> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  total: number;
}

export interface StartupProjectItem {
  id: number;
  uuid: string;
  tenantUuid: string;
  name: string;
  tagline?: string;
  description?: string;
  industry?: string;
  stage?: string;
  productDescription?: string;
  targetCustomer?: string;
  customerPain?: string;
  valueProposition?: string;
  businessModel?: string;
  teamIntroduction?: string;
  teamAdvantages?: string;
  fundingStage?: string;
  fundingAmount?: number | string | null;
  fundingPurpose?: string;
  fundingDescription?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  businessPlans?: BusinessPlanItem[];
}

export interface BusinessPlanItem {
  id: number;
  uuid: string;
  tenantUuid: string;
  projectUuid: string;
  name: string;
  versionNo: number;
  isCurrent: boolean;
  fileName: string;
  objectKey: string;
  fileSize?: number;
  mimeType?: string;
  parseStatus: string;
  parsedContentJson?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface BpAnalysisItem {
  id: number;
  uuid: string;
  tenantUuid: string;
  projectUuid: string;
  businessPlanUuid: string;
  status: string;
  summary?: string;
  strengthsJson?: unknown[] | null;
  issuesJson?: unknown[] | null;
  recommendationsJson?: unknown[] | null;
  dimensionsJson?: Record<string, unknown> | null;
  modelInfo?: string;
  promptVersion?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface InvestorPersonaItem {
  id: number;
  uuid: string;
  code: string;
  name: string;
  description?: string;
  focusAreasJson?: string[] | null;
  systemPrompt?: string;
  enabled: boolean;
  sort: number;
}

export interface PitchSimulationItem {
  id: number;
  uuid: string;
  tenantUuid: string;
  projectUuid: string;
  businessPlanUuid?: string;
  personaUuid: string;
  status: string;
  startedAt?: string;
  endedAt?: string;
  messageCount: number;
  createdAt: string;
}

export interface InvestorItem {
  uuid: string;
  name: string;
  description?: string | null;
  industries: string[];
  fundingStages: string[];
  minInvestment?: string | number | null;
  maxInvestment?: string | number | null;
  regions: string[];
  focusKeywords: string[];
  enabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface InvestorRecommendationItem {
  id: number;
  uuid: string;
  tenantUuid: string;
  projectUuid: string;
  investorUuid: string;
  matchLevel?: string;
  reasonsJson?: string[] | null;
  snapshotJson?: Record<string, unknown> | null;
  generatedAt?: string;
  investor?: InvestorItem;
}

export interface GenerateRecommendationsResult {
  items: InvestorRecommendationItem[];
  total: number;
  generatedAt: string;
}

export interface DashboardProjectSummary {
  uuid: string;
  name: string;
  stage?: string;
  fundingStage?: string;
  fundingAmount?: number | string | null;
}

export interface DashboardBpSummary {
  uuid: string;
  fileName: string;
  versionNo: number;
  parseStatus: string;
  updatedAt: string;
}

export interface DashboardAnalysisIssue {
  title: string;
  description?: string;
  priority?: string;
}

export interface DashboardAnalysisSummary {
  uuid: string;
  status: string;
  createdAt: string;
  issueCount: number;
  issues: DashboardAnalysisIssue[];
}

export interface DashboardPitchSummary {
  uuid: string;
  status: string;
  personaName?: string;
  startedAt?: string | null;
  endedAt?: string | null;
}

export interface DashboardRecommendationSummary {
  count: number;
  generatedAt?: string | null;
}

export interface DashboardNextAction {
  code: string;
  title: string;
  description: string;
}

export interface DashboardData {
  project: DashboardProjectSummary | null;
  currentBusinessPlan: DashboardBpSummary | null;
  latestAnalysis: DashboardAnalysisSummary | null;
  latestPitch: DashboardPitchSummary | null;
  recommendation: DashboardRecommendationSummary;
  nextAction: DashboardNextAction;
}

const appRequest = createAppRequest();

export {
  archiveStartupProject,
  createStartupProject,
  getStartupProjectDetail,
  getStartupProjects,
  updateStartupProject,
} from './startup-project.api';
export {
  deleteBusinessPlan,
  getBusinessPlanDetail,
  getBusinessPlanDownloadUrl,
  getBusinessPlanPreviewUrl,
  getBusinessPlans,
  getProjectBusinessPlans,
  uploadBusinessPlan,
} from './business-plan.api';

export async function getBpAnalysisDetail(
  uuid: string,
): Promise<ApiResponse<BpAnalysisItem>> {
  return appRequest(`/bff/bp-analyses/${uuid}`, {
    method: 'GET',
  });
}

export {
  cancelPitchSimulation,
  completePitchSimulation,
  createPitchSimulation,
  generateInitialQuestion,
  generatePitchInitialQuestionStream,
  getInvestorPersonas,
  getPitchReview,
  getPitchSimulationDetail,
  getPitchSimulations,
  retryPitchQuestionStream,
  sendPitchMessageStream,
} from './pitch.api';

/**
 * 投资人推荐 API
 */
export async function getInvestorRecommendations(params: {
  projectUuid: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ApiResponse<PageResult<InvestorRecommendationItem>>> {
  return appRequest('/bff/investor-recommendations', {
    method: 'GET',
    params,
  });
}

export async function generateInvestorRecommendations(data: {
  projectUuid: string;
}): Promise<ApiResponse<GenerateRecommendationsResult>> {
  return appRequest('/bff/investor-recommendations/generate', {
    method: 'POST',
    data,
  });
}

/**
 * 工作台聚合 API
 */
export async function getDashboardData(params?: {
  projectUuid?: string;
}): Promise<ApiResponse<DashboardData>> {
  return appRequest('/bff/dashboard', {
    method: 'GET',
    params,
  });
}

